import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';

import {
  exchangesSelector,
  getExchanges,
  getGivenExchangesLabels,
  getReceivedExchangesLabels,
  givenFiltersSelector,
  receivedFiltersSelector,
  setReceiveExchanges,
} from 'src/store/exchanges';

import { REACT_APP_API_URL } from 'src/constants';

import { ReactComponent as SearchIcon } from 'src/assets/exchange/SearchIcon.svg';
import './exchange.scss';
import { CustomInput } from 'src/components';

export const Exchange = () => {
  const { t, i18n } = useTranslation('exchange');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const exchanges = useSelector(exchangesSelector);

  const givenFilters = useSelector(givenFiltersSelector);
  const receivedFilters = useSelector(receivedFiltersSelector);

  const [selectedGive, setSelectedGive] = useState({});

  const [selectedGiveFilter, setSelectedGiveFilter] = useState('');
  const [selectedReceiveFilter, setSelectedReceiveFilter] = useState('');

  const [givenData, setGivenData] = useState([]);
  const [receivedData, setReceivedData] = useState([]);

  useEffect(() => {
    dispatch(getExchanges());
    dispatch(getGivenExchangesLabels());
  }, [dispatch, i18n.language]);

  useEffect(() => {
    if (!_.isEmpty(selectedGive)) {
      dispatch(getReceivedExchangesLabels(selectedGive?.id));
    }
  }, [dispatch, selectedGive, i18n.language]);

  useEffect(() => {
    setGivenData(exchanges || []);
    setReceivedData(exchanges?.[0]?.currency || []);
    setSelectedGive(exchanges?.[0] || {});
    dispatch(setReceiveExchanges(exchanges[0]?.currency || []));
  }, [dispatch, exchanges]);

  const onClickGiveItem = useCallback(
    (item) => () => {
      setReceivedData(item?.currency);
      setSelectedGive(item);
      dispatch(setReceiveExchanges(item?.currency));
    },
    [dispatch],
  );

  const onClickReceiveItem = useCallback(
    (item) => () => {
      navigate(`/exchange/${item?.exchange_id}/create`);
    },
    [navigate],
  );

  const onSelectGivenFilter = useCallback(
    (filter) => () => {
      setSelectedGiveFilter(filter);

      dispatch(getExchanges({ given: filter }));
    },
    [dispatch],
  );

  const onSelectReceivedFilter = useCallback(
    (filter) => () => {
      setSelectedReceiveFilter(filter);

      const filteredList = selectedGive?.currency?.filter((item) =>
        item?.label?.toLowerCase()?.includes(filter?.toLowerCase()),
      );

      setReceivedData(filteredList);
    },
    [selectedGive?.currency],
  );

  const onChangeGivenSearch = useCallback(
    (event) => {
      const value = event.target.value;

      const filteredList = exchanges?.filter(
        (item) =>
          item?.name?.toLowerCase()?.includes(value?.toLowerCase()) ||
          item?.code?.toLowerCase()?.includes(value?.toLowerCase()),
      );

      setGivenData(filteredList);
    },
    [exchanges],
  );

  const onChangeReceivedSearch = useCallback(
    (event) => {
      const value = event.target.value;

      const filteredList = selectedGive?.currency?.filter(
        (item) =>
          item?.name?.toLowerCase()?.includes(value?.toLowerCase()) ||
          item?.code?.toLowerCase()?.includes(value?.toLowerCase()),
      );

      setReceivedData(filteredList);
    },
    [selectedGive],
  );

  const renderSection = useCallback(
    (props) => {
      const {
        title,
        filter,
        filters,
        onClick = () => {},
        onChangeSearch = () => {},
        onSelectFilter = () => {},
        data = [],
        id,
      } = props;

      return (
        <div className="exchange__section">
          <div className="exchange__section__title">
            <h3>{title}</h3>
            <div className="exchange__section__filters">
              {filters.map((item) => {
                const buttonStyles = classNames('exchange__section__button', {
                  'exchange__section__button--active': filter === item.name,
                });

                return (
                  <Button
                    color="default"
                    variant="filled"
                    className={buttonStyles}
                    key={item.name}
                    onClick={onSelectFilter(item?.name)}
                  >
                    {item.translate}
                  </Button>
                );
              })}
            </div>
          </div>
          <div className="exchange__section__search">
            <CustomInput
              placeholder={t('search')}
              suffix={<SearchIcon />}
              onChange={onChangeSearch}
            />
          </div>
          <div className="exchange__section__list">
            {data.map((item, index) => (
              <div
                className={`exchange__section__item ${
                  item?.id === id ? 'exchange__section__item--selected' : ''
                }`}
                key={item.id}
                onClick={onClick(item)}
              >
                <div className="exchange__section__item__image">
                  <img
                    src={REACT_APP_API_URL + item?.image}
                    alt={REACT_APP_API_URL + item?.image}
                  />
                </div>
                <div>
                  {item?.name} <span>({item?.code})</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    },
    [t],
  );

  return (
    <div className="exchange">
      {renderSection({
        title: t('give'),
        filter: selectedGiveFilter,
        filters: givenFilters,
        onClick: onClickGiveItem,
        onChangeSearch: onChangeGivenSearch,
        onSelectFilter: onSelectGivenFilter,
        data: givenData,
        id: selectedGive?.id,
      })}

      {renderSection({
        title: t('receive'),
        filter: selectedReceiveFilter,
        filters: receivedFilters,
        onClick: onClickReceiveItem,
        onChangeSearch: onChangeReceivedSearch,
        onSelectFilter: onSelectReceivedFilter,
        data: receivedData,
        id: {},
      })}
    </div>
  );
};
